/**
 * The overview rail (docs/ux/matter-hub.md): the record panel, the side-steps
 * checklist, the outstanding items summary; the items tab; the not-yet tabs.
 * Reading rhythm: 16 px between pairs, 24 px between panels.
 */

import { reportTypeLabel, statusLabel } from '@beam/core/status';
import { cn } from 'cn';
import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Appointment, MatterDetail, OutstandingItem, SideStepKey } from '@/app/types';
import { Button, buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EmptyState } from '@/components/shell/states';
import { dateTime, isPast, localDateKey, longDate, shortDate } from '@/lib/format';

export function Panel({ title, children, className, action }: { title: string; children: ReactNode; className?: string; action?: ReactNode }) {
  return (
    <section aria-label={title} className={cn('rounded-xl border border-beam-line bg-beam-paper p-4', className)}>
      <header className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="eyebrow text-beam-text-muted">{title}</h2>
        {action}
      </header>
      {children}
    </section>
  );
}

function Pair({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-beam-text-muted">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export function MatterRecordPanel({ detail, offline, onMarkPaid, paying, onUrgent, urgentPending }: { detail: MatterDetail; offline: boolean; onMarkPaid: () => void; paying: boolean; onUrgent?: (urgent: boolean) => void; urgentPending?: boolean }) {
  const { matter, cycle } = detail;
  const plaintiff = matter.referrer.kind === 'plaintiff';
  const past = isPast(`${matter.dueDate}T23:59:59`);
  return (
    <Panel title="Record">
      <dl className="grid grid-cols-2 gap-4 md:grid-cols-1">
        <Pair label="Patient (as on LOI)">{matter.patientAsOnLoi}</Pair>
        <Pair label="Referrer">{matter.referrer.name}{plaintiff ? <span className="text-beam-text-muted"> (plaintiff)</span> : null}</Pair>
        <Pair label="Doctor">{matter.doctor.name}</Pair>
        <Pair label="Type">{reportTypeLabel(matter.reportType, 'label')}</Pair>
        <Pair label="Due"><span className={cn('tabular', past && 'font-semibold text-beam-error')}>{longDate(matter.dueDate)}</span></Pair>
        {matter.status >= 9 ? (
          <Pair label="Billing">{cycle.billingBand ? <>{cycle.billingBand}{cycle.billingCode ? <span className="text-beam-text-muted"> · {cycle.billingCode}</span> : null}</> : <span className="text-beam-text-muted">Not set</span>}</Pair>
        ) : null}
        {plaintiff && matter.status >= 9 ? (
          <Pair label="Payment">
            {cycle.gates.paymentReceived ? (
              <span>Received{cycle.paymentReceivedAt ? ` ${shortDate(cycle.paymentReceivedAt)}` : ''}</span>
            ) : (
              <span className="flex flex-wrap items-center gap-2">
                Not received
                {matter.status === 11 ? <Button variant="outline" size="default" className="h-11 md:h-8" disabled={offline || paying} onClick={onMarkPaid}>{paying ? 'Saving' : 'Mark received'}</Button> : null}
              </span>
            )}
          </Pair>
        ) : null}
        {onUrgent ? (
          <Pair label="Priority">
            <span className="flex flex-wrap items-center gap-2">
              {cycle.flags.urgent ? <span className="font-medium text-beam-error">Urgent</span> : <span className="text-beam-text-muted">Not urgent</span>}
              <Button variant="outline" size="default" className="h-11 md:h-8" data-slot="urgent-toggle" disabled={offline || urgentPending} aria-busy={urgentPending} onClick={() => onUrgent(!cycle.flags.urgent)}>
                {urgentPending ? 'Saving' : cycle.flags.urgent ? 'Clear urgent' : 'Mark urgent'}
              </Button>
            </span>
          </Pair>
        ) : null}
        <Pair label="Key dates">
          <span className="flex flex-wrap gap-x-3 gap-y-1 text-[13px]">
            {matter.keyDates.referralReceived ? <span>Referral {shortDate(matter.keyDates.referralReceived)}</span> : null}
            {matter.keyDates.approved ? <span>Approved {shortDate(matter.keyDates.approved)}</span> : null}
            {matter.keyDates.paperworkReceived ? <span>Paperwork {shortDate(matter.keyDates.paperworkReceived)}</span> : null}
            {matter.keyDates.appointment ? <span>Appointment {shortDate(matter.keyDates.appointment)}</span> : null}
            {matter.keyDates.released ? <span>Released {shortDate(matter.keyDates.released)}</span> : null}
          </span>
        </Pair>
        {matter.status === 13 && cycle.parkedFrom !== null ? (
          <Pair label="Parked">Parked from {cycle.parkedFrom} {statusLabel(cycle.parkedFrom)}{cycle.parkedAt ? ` on ${shortDate(cycle.parkedAt)}` : ''}. Resume returns it there.</Pair>
        ) : null}
      </dl>
    </Panel>
  );
}

type StepRow = { key: SideStepKey; label: string; date?: string; checked: boolean; sub?: { key: SideStepKey; label: string; checked: boolean } };

export function sideStepRows(s: MatterDetail['cycle']['sideSteps']): StepRow[] {
  return [
    { key: 'referrerConfirmed', label: 'Referrer confirmed', date: s.referrerConfirmedAt, checked: Boolean(s.referrerConfirmedAt) },
    { key: 'consentSent', label: 'Consent sent', date: s.consent.sentAt, checked: Boolean(s.consent.sentAt) },
    { key: 'consentReturned', label: 'Consent returned', date: s.consent.returnedAt, checked: Boolean(s.consent.returnedAt), sub: { key: 'consentInClinic', label: 'Completed in clinic', checked: Boolean(s.consent.inClinic) } },
    { key: 'questionnaireComplete', label: 'Questionnaire complete', date: s.questionnaire.completedAt, checked: Boolean(s.questionnaire.completedAt), sub: { key: 'questionnaireInClinic', label: 'Completed in clinic', checked: Boolean(s.questionnaire.inClinic) } },
    { key: 'drAllocationConfirmed', label: 'Dr allocation confirmed', date: s.drAllocationConfirmedAt, checked: Boolean(s.drAllocationConfirmedAt) },
  ];
}

export function SideStepsChecklist({ sideSteps, offline, onSet, pendingKey }: { sideSteps: MatterDetail['cycle']['sideSteps']; offline: boolean; onSet: (key: SideStepKey, value: boolean, label: string) => void; pendingKey: SideStepKey | null }) {
  const rows = sideStepRows(sideSteps);
  return (
    <fieldset className="rounded-xl border border-beam-line bg-beam-paper p-4" disabled={offline} title={offline ? 'Actions are paused while offline' : undefined}>
      <legend className="eyebrow px-1 text-beam-text-muted">Side-steps</legend>
      <ul className="flex flex-col">
        {rows.map((r) => (
          <li key={r.key} className="flex flex-col">
            <StepLine key={r.key} row={r} onSet={onSet} pending={pendingKey === r.key} />
            {r.sub ? <StepLine row={{ key: r.sub.key, label: r.sub.label, checked: r.sub.checked }} indent onSet={onSet} pending={pendingKey === r.sub.key} /> : null}
          </li>
        ))}
      </ul>
    </fieldset>
  );
}

function StepLine({ row, indent, onSet, pending }: { row: Omit<StepRow, 'sub'>; indent?: boolean; onSet: (key: SideStepKey, value: boolean, label: string) => void; pending: boolean }) {
  const id = `step-${row.key}`;
  return (
    <div className={cn('flex min-h-11 items-center gap-3 md:min-h-9', indent && 'pl-6')} data-step={row.key}>
      <Checkbox id={id} checked={row.checked} disabled={pending} onCheckedChange={(v) => onSet(row.key, v === true, row.label)} aria-describedby={row.date ? `${id}-date` : undefined} />
      <Label htmlFor={id} className="min-h-11 flex-1 cursor-pointer items-center text-sm font-normal md:min-h-9">{row.label}</Label>
      {row.date ? (
        <span id={`${id}-date`} className="tabular text-xs text-beam-text-muted">
          <span aria-hidden="true">{shortDate(row.date)}</span>
          <span className="sr-only">set {longDate(row.date)}</span>
        </span>
      ) : null}
    </div>
  );
}

export function OutstandingItemsSummary({ items }: { items: OutstandingItem[] }) {
  const open = items.filter((i) => !i.resolvedAt);
  if (items.length === 0) return null;
  const next = open.map((i) => i.followUpAt).sort()[0];
  return (
    <Panel title="Outstanding items">
      <p className="text-sm">
        {open.length === 0 ? 'No open items.' : `${open.length} outstanding ${open.length === 1 ? 'item' : 'items'} open.${next ? ` Next follow up ${shortDate(next)}.` : ''}`}{' '}
        <Link to="?tab=items" className="text-beam-signature underline-offset-4 hover:underline">Open the items tab</Link>
      </p>
    </Panel>
  );
}

export function OutstandingItems({ items, offline, onAdd, onResolve, adding, resolvingId }: {
  items: OutstandingItem[];
  offline: boolean;
  /** Resolves when the item is saved; rejects on failure (the hook has already toasted). */
  onAdd: (v: { description: string; followUpAt: string }) => Promise<void>;
  onResolve: (id: string) => void;
  adding: boolean;
  resolvingId: string | null;
}) {
  const [description, setDescription] = useState('');
  const [followUpAt, setFollowUpAt] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 7); return localDateKey(d); });
  const [error, setError] = useState<string | null>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const open = items.filter((i) => !i.resolvedAt).sort((a, b) => a.followUpAt.localeCompare(b.followUpAt));
  const resolved = items.filter((i) => i.resolvedAt).sort((a, b) => (b.resolvedAt ?? '').localeCompare(a.resolvedAt ?? ''));
  const today = localDateKey();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (description.trim().length === 0) { setError('Describe what is outstanding'); return; }
    if (followUpAt < today) { setError('Choose today or a later date'); return; }
    setError(null);
    try {
      await onAdd({ description: description.trim(), followUpAt });
      // Only a saved item clears the row; a failure keeps the typed text for the retry.
      setDescription('');
      descriptionRef.current?.focus();
    } catch {
      // The mutation's toast has said what did not happen.
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={submit} className="flex flex-col gap-3 rounded-xl border border-beam-line bg-beam-paper p-4 md:flex-row md:flex-wrap md:items-end" noValidate>
        <div className="flex flex-1 flex-col gap-1">
          <Label htmlFor="item-description">Description</Label>
          <Input ref={descriptionRef} id="item-description" value={description} onChange={(e) => setDescription(e.target.value)} aria-invalid={error ? true : undefined} aria-describedby={error ? 'item-error' : undefined} className="h-11 text-base md:h-9 md:text-sm" disabled={offline} />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="item-date">Follow up by</Label>
          <Input id="item-date" type="date" min={today} value={followUpAt} onChange={(e) => setFollowUpAt(e.target.value)} className="h-11 text-base md:h-9 md:text-sm" disabled={offline} />
        </div>
        <Button type="submit" variant="outline" size="lg" className="h-11 md:h-9" disabled={offline || adding}>{adding ? 'Adding' : 'Add item'}</Button>
        {error ? <p id="item-error" role="alert" className="text-[13px] text-beam-error md:w-full">{error}</p> : null}
      </form>
      <Panel title={`Open (${open.length})`}>
        {open.length === 0 ? <p className="text-beam-text-muted">{items.length === 0 ? 'No outstanding items.' : 'No open items.'}</p> : (
          <ul className="flex flex-col divide-y divide-beam-line">
            {open.map((i) => {
              const late = i.followUpAt < today;
              return (
                <li key={i.itemId} className="flex min-h-12 items-center justify-between gap-3 py-2">
                  <div className="flex flex-col">
                    <span>{i.description}</span>
                    <span className={cn('tabular text-xs', late ? 'font-semibold text-beam-error' : 'text-beam-text-muted')}>Follow up by {shortDate(i.followUpAt)}{late ? ` (${daysAgo(i.followUpAt, today)} ago)` : ''}</span>
                  </div>
                  <Button variant="outline" size="default" className="h-11 md:h-8" aria-label={`Resolve: ${i.description}`} disabled={offline || resolvingId === i.itemId} onClick={() => onResolve(i.itemId)}>Resolve</Button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
      {resolved.length > 0 ? (
        <Panel title={`Resolved (${resolved.length})`}>
          <ul className="flex flex-col divide-y divide-beam-line text-beam-text-muted">
            {resolved.map((i) => (
              <li key={i.itemId} className="flex min-h-11 flex-col justify-center py-2">
                <span>{i.description}</span>
                <span className="tabular text-xs">Resolved {i.resolvedAt ? shortDate(i.resolvedAt) : ''}{i.resolvedBy ? ` by ${i.resolvedBy}` : ''}</span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
      <p className="text-xs text-beam-text-muted">Chasers to the referrer are not sent from here in this proof of concept; the item and its follow-up date are the record.</p>
    </div>
  );
}

function daysAgo(date: string, today: string): string {
  const n = Math.round((new Date(today).getTime() - new Date(date).getTime()) / 86_400_000);
  return `${n} ${n === 1 ? 'day' : 'days'}`;
}

export const NOT_YET: Record<string, { title: string; description: string }> = {
  release: { title: 'Release arrives in milestone 9.', description: 'It will show the finalisation checklist and the release pack.' },
  bridge: { title: 'The Quality Scribe bridge arrives in milestone 10.', description: 'It will show audio in, packaging and ingest.' },
  booking: { title: 'Booking arrives in milestone 11.', description: 'The appointment is keyed there. Appointments already on this case are listed below.' },
};

export function NotYetTab({ tab, appointments }: { tab: string; appointments: Appointment[] }) {
  const copy = NOT_YET[tab] ?? { title: 'This tab arrives in a later milestone.', description: '' };
  return (
    <div className="flex flex-col gap-4">
      <EmptyState title={copy.title} description={copy.description} />
      {tab === 'booking' ? (
        appointments.length === 0 ? <p className="text-beam-text-muted">No appointment recorded.</p> : (
          <Panel title="Appointments">
            <ul className="flex flex-col divide-y divide-beam-line">
              {appointments.map((a) => (
                <li key={a.apptId} className="flex min-h-11 flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span className="tabular">{dateTime(a.startsAt)}</span>
                  <span>{a.clinicName}</span>
                  <span className="text-beam-text-muted">{a.attendance ? a.attendance === 'dna' ? 'Did not attend' : a.attendance === 'attended' ? 'Attended' : 'Cancelled' : a.confirmed ? 'Confirmed' : 'Not confirmed'}</span>
                </li>
              ))}
            </ul>
          </Panel>
        )
      ) : null}
    </div>
  );
}

export function MatterNotFound({ matterRef, home }: { matterRef: string; home: string }) {
  return (
    <EmptyState
      className="mt-6"
      title={`No case ${matterRef}`}
      description="Check the reference, or search by surname."
      action={<Link to={home} className={buttonVariants({ variant: 'outline' })}>Go to your home</Link>}
    />
  );
}
